import { transformError as sdkTransformError } from '@wix/sdk-runtime/transform-error';
import { queryBuilder } from '@wix/sdk-runtime/query-builder';
import {
  renameKeysFromSDKRequestToRESTRequest,
  renameKeysFromRESTResponseToSDKResponse,
} from '@wix/sdk-runtime/rename-all-nested-keys';
import {
  HttpClient,
  HttpResponse,
  NonNullablePaths,
  QuerySpec,
  Query as QuerySdkType,
} from '@wix/sdk-types';
import * as ambassadorWixPremiumResellerV1Packages from './premium-reseller-v1-packages-packages.http.js';
// @ts-ignore
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { createQueryUtils } from '@wix/sdk-runtime/query-builder-utils';

/**
 * A package is group of instances of Wix services that a reseller offers to a
 * customer as part of a single transaction.
 */
export interface Package {
  /**
   * Package ID.
   * @readonly
   * @format GUID
   */
  _id?: string;
  /**
   * Wix account ID. See
   * [Account Level APIs](https://dev.wix.com/docs/rest/account-level/about-account-level-apis)
   * for more details.
   * @readonly
   * @format GUID
   */
  accountId?: string;
  /**
   * External reference. For example, an external subscription ID.
   * This field isn't validated by Wix.
   *
   * Max: 100 characters
   * @maxLength 100
   */
  externalId?: string | null;
  /**
   * Product instances that are included in the package.
   *
   * Min: 1 product instance
   * Max: 1000 product instances
   * @maxSize 1000
   * @minSize 1
   */
  productInstances?: ProductInstance[];
  /**
   * Date and time the package was created.
   * @readonly
   */
  _createdDate?: Date | null;
  /**
   * Date and time the package was last updated.
   * @readonly
   */
  _updatedDate?: Date | null;
}

/**
 * A product instance is a specific instance of a Wix service that a reseller
 * offers to one of their customers.
 */
export interface ProductInstance extends ProductInstanceContractDetailsOneOf {
  /** Billing information for the contract between the reseller and Wix. */
  billingInfo?: ProductInstanceCycle;
  /**
   * ID of the instance of the resold Wix service. **Note:** Differs from the
   * `catalogProductId`. Allows you to identify different instances of the same
   * product in a package.
   * @readonly
   * @format GUID
   */
  instanceId?: string;
  /**
   * ID of the Wix site that the product instance is assigned to.
   * See the [Query Sites API](https://dev.wix.com/api/rest/account-level-apis/sites/query-sites)
   * for more information.
   * @format GUID
   */
  siteId?: string | null;
  /**
   * Product ID, as defined in the Wix Premium Product Catalog.
   * Contact the [Wix B2B sales team](mailto:bizdev@wix.com)
   * for more details about available Wix services.
   * @format GUID
   */
  catalogProductId?: string;
  /**
   * Status of the product instance.
   * @readonly
   */
  status?: StatusWithLiterals;
  /**
   * Failure object. Only present for status `FAILED`. Describes why the product instance `FAILED`.
   * @readonly
   */
  failure?: FailureReason;
  /**
   * Date and time the product instance was created.
   * @readonly
   */
  _createdDate?: Date | null;
  /**
   * Date and time the product instance was last updated.
   * @readonly
   */
  _updatedDate?: Date | null;
  /**
   * Two-letter country code in
   * [ISO-3166 alpha-2](https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2#Officially_assigned_code_elements)
   * format. Specifies where customers can claim vouchers that may come with the
   * product. Contact the [Wix B2B sales team](mailto:bizdev@wix.com) to get more
   * information about vouchers and supported locations.
   * @format COUNTRY
   */
  countryCode?: string | null;
  /**
   * Date and time the product instance expires in `YYYY-MM-DDThh:mm:ss.sssZ` format.
   * Used only for instances that don't auto renew at the end of the current billing cycle.
   * @readonly
   */
  expirationDate?: Date | null;
  /**
   * Discount code indicating that the reseller provisioned the product instance
   * during a sale. Wix doesn't guarantee that a discount code reduces the
   * price between Wix and the reseller, even when it's valid. Contact the
   * [Wix B2B sales team](mailto:bizdev@wix.com) for more information.
   *
   * Max: 25 characters
   * @maxLength 25
   */
  discountCode?: string | null;
  /**
   * ID of a different product instance that you can use to offer your customers
   * time-limited free access to an additional product or service. For example,
   * Wix offers a 1-year free domain registration to all customers who purchase a
   * Premium plan. The referenced product instance must have either status `"PENDING"` or
   * `"ACTIVE"`. You can use each product instance only a single time as reference
   * instance.
   * @format GUID
   */
  referenceProductInstanceId?: string | null;
}

/** @oneof */
export interface ProductInstanceContractDetailsOneOf {
  /** Billing information for the contract between the reseller and Wix. */
  billingInfo?: ProductInstanceCycle;
}

export enum FailureReasonCode {
  /** The product instance couldn't be created because the Resellers API timed out. */
  DELIVERY_TIMEOUT = 'DELIVERY_TIMEOUT',
  /** The product instance couldn't be created because an external process failed. */
  EXTERNAL_FAILURE = 'EXTERNAL_FAILURE',
}

/** @enumType */
export type FailureReasonCodeWithLiterals =
  | FailureReasonCode
  | 'DELIVERY_TIMEOUT'
  | 'EXTERNAL_FAILURE';

export enum IntervalIntervalUnit {
  /** Day */
  DAY = 'DAY',
  /** Week */
  WEEK = 'WEEK',
  /** Month */
  MONTH = 'MONTH',
  /** Year */
  YEAR = 'YEAR',
}

/** @enumType */
export type IntervalIntervalUnitWithLiterals =
  | IntervalIntervalUnit
  | 'DAY'
  | 'WEEK'
  | 'MONTH'
  | 'YEAR';

export enum CycleDescriptorType {
  /** The reseller pays Wix in a single payment. */
  ONE_TIME = 'ONE_TIME',
  /** The reseller pays Wix on a recurring schedule. */
  RECURRING = 'RECURRING',
}

/** @enumType */
export type CycleDescriptorTypeWithLiterals =
  | CycleDescriptorType
  | 'ONE_TIME'
  | 'RECURRING';

export interface CycleInterval {
  /** Unit of the billing cycle. */
  unit?: IntervalIntervalUnitWithLiterals;
  /**
   * Count of units that make up the billing cycle.
   * @min 1
   */
  count?: number;
}

export enum Status {
  /** The product instance isn't yet available to the customer. */
  PENDING = 'PENDING',
  /** The customer can use the product instance. */
  ENABLED = 'ENABLED',
  /** The product instance isn't any longer available to the customer. */
  CANCELED = 'CANCELED',
  /** The product instance couldn't be delivered successfully and has never been available to the customer. */
  FAILED = 'FAILED',
  /** The product instance isn't yet available to the customer, because an external provider or the customer must take an action. */
  AWAITING_ACTION = 'AWAITING_ACTION',
}

/** @enumType */
export type StatusWithLiterals =
  | Status
  | 'PENDING'
  | 'ENABLED'
  | 'CANCELED'
  | 'FAILED'
  | 'AWAITING_ACTION';

export interface FailureReason {
  /** Failure code. */
  code?: FailureReasonCodeWithLiterals;
  /** Failure message. */
  message?: string;
}

export interface ProductInstanceCycle {
  /** Payment type. */
  type?: CycleDescriptorTypeWithLiterals;
  /** Duration of the billing cycle. Available only for `RECURRING` payments. */
  cycleDuration?: CycleInterval;
}

export interface CreatePackageRequest {
  /**
   * Idempotency key.
   * @maxLength 100
   */
  idempotencyKey?: string | null;
  /**
   * External reference. For example, an external subscription ID. **Note:** This field is not validated by Wix.
   * @maxLength 100
   */
  externalId?: string | null;
  /**
   * Wix services that are resold.
   * @maxSize 1000
   * @minSize 1
   */
  products: ProductInstance[];
}

export interface CreatePackageResponse {
  /**
   * Idempotency key.
   * @maxLength 100
   */
  idempotencyKey?: string | null;
  /** Created package. */
  package?: Package;
}

export interface CancelPackageRequest {
  /**
   * Idempotency key.
   * @maxLength 100
   */
  idempotencyKey?: string | null;
  /**
   * ID of the package to cancel.
   * @format GUID
   */
  _id: string;
}

export interface CancelPackageResponse {
  /**
   * Idempotency key.
   * @maxLength 100
   */
  idempotencyKey?: string | null;
  /** Canceled package. */
  package?: Package;
}

export interface PackageCanceled {
  /** Canceled package. */
  package?: Package;
}

export interface GetPackageRequest {
  /**
   * Package ID.
   * @format GUID
   */
  _id: string;
}

export interface GetPackageResponse {
  /** Retrieved package. */
  package?: Package;
}

export interface QueryPackagesRequest {
  /** Query options. See [API Query Language](https://dev.wix.com/api/rest/getting-started/api-query-language) for more details. */
  query: QueryV2;
}

export interface QueryV2 extends QueryV2PagingMethodOneOf {
  /** Paging options to limit and offset the number of items. */
  paging?: Paging;
  /** Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`. */
  cursorPaging?: CursorPaging;
  /**
   * Filter object.
   *
   * Learn more about [filtering](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#filters).
   */
  filter?: Record<string, any> | null;
  /**
   * Sort object.
   *
   * Learn more about [sorting](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#sorting).
   */
  sort?: Sorting[];
  /** Array of projected fields. A list of specific field names to return. If `fieldsets` are also specified, the union of `fieldsets` and `fields` is returned. */
  fields?: string[];
  /** Array of named, predefined sets of projected fields. A array of predefined named sets of fields to be returned. Specifying multiple `fieldsets` will return the union of fields from all sets. If `fields` are also specified, the union of `fieldsets` and `fields` is returned. */
  fieldsets?: string[];
}

/** @oneof */
export interface QueryV2PagingMethodOneOf {
  /** Paging options to limit and offset the number of items. */
  paging?: Paging;
  /** Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`. */
  cursorPaging?: CursorPaging;
}

export interface Sorting {
  /**
   * Name of the field to sort by.
   * @maxLength 512
   */
  fieldName?: string;
  /** Sort order. */
  order?: SortOrderWithLiterals;
}

export enum SortOrder {
  ASC = 'ASC',
  DESC = 'DESC',
}

/** @enumType */
export type SortOrderWithLiterals = SortOrder | 'ASC' | 'DESC';

export interface Paging {
  /** Number of items to load. */
  limit?: number | null;
  /** Number of items to skip in the current sort order. */
  offset?: number | null;
}

export interface CursorPaging {
  /**
   * Maximum number of items to return in the results.
   * @max 100
   */
  limit?: number | null;
  /**
   * Pointer to the next or previous page in the list of results.
   *
   * Pass the relevant cursor token from the `pagingMetadata` object in the previous call's response.
   * Not relevant for the first request.
   * @maxLength 16000
   */
  cursor?: string | null;
}

export interface QueryPackagesResponse {
  /** Paging metadata. */
  metadata?: PagingMetadataV2;
  /**
   * Retrieved packages.
   *
   * Max: 1000 packages
   * @maxSize 1000
   */
  packages?: Package[];
}

export interface PagingMetadataV2 {
  /** Number of items returned in the response. */
  count?: number | null;
  /** Offset that was requested. */
  offset?: number | null;
  /** Total number of items that match the query. Returned if offset paging is used and the `tooManyToCount` flag is not set. */
  total?: number | null;
  /** Flag that indicates the server failed to calculate the `total` field. */
  tooManyToCount?: boolean | null;
  /** Cursors to navigate through the result pages using `next` and `prev`. Returned if cursor paging is used. */
  cursors?: Cursors;
}

export interface Cursors {
  /**
   * Cursor string pointing to the next page in the list of results.
   * @maxLength 16000
   */
  next?: string | null;
  /**
   * Cursor pointing to the previous page in the list of results.
   * @maxLength 16000
   */
  prev?: string | null;
}

export interface AssignProductInstanceToSiteRequest {
  /**
   * Idempotency key.
   * @maxLength 100
   */
  idempotencyKey?: string | null;
  /**
   * ID of the product instance to assign to a site.
   * @format GUID
   */
  instanceId: string;
  /**
   * ID of the site to which the product instance is assigned.
   * @format GUID
   */
  siteId: string;
}

export interface AssignProductInstanceToSiteResponse {
  /**
   * Idempotency key.
   * @maxLength 100
   */
  idempotencyKey?: string | null;
  /** Updated package that includes the assigned product instance. */
  package?: Package;
}

export interface AssignedProductInstanceToSite {
  /** Updated package. */
  package?: Package;
  /**
   * ID of the product instance that was assigned to the site.
   * @readonly
   * @format GUID
   */
  productInstanceId?: string;
  /**
   * ID of the site the product instance was assigned to.
   * @readonly
   * @format GUID
   */
  siteId?: string;
}

export interface UnassignProductInstanceFromSiteRequest {
  /**
   * Idempotency key.
   * @maxLength 100
   */
  idempotencyKey?: string | null;
  /**
   * ID of the product instance to unassign from a site.
   * @format GUID
   */
  instanceId: string;
}

export interface UnassignProductInstanceFromSiteResponse {
  /**
   * Idempotency key.
   * @maxLength 100
   */
  idempotencyKey?: string | null;
  /** Updated package that includes the unassigned product instance. */
  package?: Package;
}

export interface UnassignedProductInstanceFromSite {
  /** Updated package. */
  package?: Package;
  /**
   * ID of the product instance that was unassigned from the site.
   * @readonly
   * @format GUID
   */
  productInstanceId?: string;
  /**
   * MetasiteId of the site the product instance was unassigned from.
   * @readonly
   * @format GUID
   */
  metaSiteId?: string;
}

export interface CancelProductInstanceRequest {
  /**
   * Idempotency key.
   * @maxLength 100
   */
  idempotencyKey?: string | null;
  /**
   * ID of the product instance to cancel.
   * @format GUID
   */
  instanceId: string;
}

export interface CancelProductInstanceResponse {
  /**
   * Idempotency key.
   * @maxLength 100
   */
  idempotencyKey?: string | null;
  /** Updated package that includes the previously included the canceled product instance. */
  package?: Package;
}

export interface ProductInstanceCanceled {
  /** Updated package. */
  package?: Package;
  /**
   * ID of the product instance that was canceled.
   * @readonly
   * @format GUID
   */
  productInstanceId?: string;
}

export interface AdjustProductInstanceSpecificationsRequest {
  /**
   * Idempotency key.
   * @maxLength 100
   */
  idempotencyKey?: string | null;
  /**
   * ID of the product instance to adjust.
   * @format GUID
   */
  instanceId: string;
  /**
   * ID of the product to replace the original instance. Required in case you don't
   * provide a new `billingInfo` object.
   * @format GUID
   */
  catalogProductId?: string | null;
  /**
   * Information about the billing cycle. Required in case you don't provide a new
   * `catalogProductId`. The new billing cycle must be supported for the service.
   * Contact the [Wix B2B sales team](mailto:bizdev@wix.com) for
   * information about a service's supported billing cycles. If
   * you adjust the billing cycle for a product instance with `RECURRING`
   * payments, you must also provide `billingInfo.cycleDuration.unit`.
   */
  billingInfo?: ProductInstanceCycle;
  /**
   * Discount code indicating that the reseller provisioned the product instance
   * during a sale. In case you pass a code that isn't valid, the call succeeds and
   * no discount is applied to the product instance. Wix doesn't guarantee that a
   * discount code reduces the price between Wix and the reseller, even when it's
   * valid. You can't add a discount code after you've created the
   * product instance. Contact the [Wix B2B sales team](mailto:bizdev@wix.com) for
   * more information about supported codes.
   *
   * Max: 25 characters
   * @maxLength 25
   */
  discountCode?: string | null;
}

export interface AdjustProductInstanceSpecificationsResponse {
  /**
   * Idempotency key.
   * @maxLength 100
   */
  idempotencyKey?: string | null;
  /** Updated package that includes the adjusted product instance. */
  package?: Package;
}

export interface AdjustedProductInstanceSpecifications {
  /** Updated package. */
  package?: Package;
  /**
   * ID of the product instance to adjust.
   * @readonly
   * @format GUID
   */
  productInstanceId?: string;
}

export interface SubscriptionEvent extends SubscriptionEventEventOneOf {
  /** Triggered when a subscription is created. */
  created?: SubscriptionCreated;
  /**
   * Triggered when a subscription is assigned to a Wix site, including the initial
   * assignment of a floating subscription or a re-assignement from a different site.
   */
  assigned?: SubscriptionAssigned;
  /** Triggered when a subscription is canceled. */
  cancelled?: SubscriptionCancelled;
  /** Triggered when the subscription's auto renew is turned on. */
  autoRenewTurnedOn?: SubscriptionAutoRenewTurnedOn;
  /** Triggered when the subscription's auto renew is turned off. */
  autoRenewTurnedOff?: SubscriptionAutoRenewTurnedOff;
  /**
   * Triggered when a subscription is unassigned from a Wix site and becomes
   * floating.
   */
  unassigned?: SubscriptionUnassigned;
  /**
   * Triggered when a subscription is transferred from one Wix account to another.
   * A transfer includes cancelling the original subscription and creating a new
   * subscription for the target account. The event returns both the original
   * and the new subscription.
   */
  transferred?: SubscriptionTransferred;
  /** Triggered when a recurring charge succeeds for a subscription. */
  recurringChargeSucceeded?: RecurringChargeSucceeded;
  /**
   * Triggered when a subscription was updated including when its product has been
   * up- or downgraded or the billing cycle is changed.
   */
  contractSwitched?: ContractSwitched;
  /**
   * Triggered when a subscription gets close to the end of its billing cycle.
   * The exact number of days is defined in the billing system.
   */
  nearEndOfPeriod?: SubscriptionNearEndOfPeriod;
  /**
   * Triggered when a subscription is updated and the change doesn't happen
   * immediately but at the end of the current billing cycle.
   */
  pendingChange?: SubscriptionPendingChange;
  /** Triggered when a recurring charge attempt fails for a subscription. */
  recurringChargeAttemptFailed?: RecurringChargeAttemptFailed;
  /**
   * ID of the subscription's event.
   * @format GUID
   */
  eventId?: string | null;
  /**
   * Date and time of the event in
   * [UTC datetime](https://en.wikipedia.org/wiki/Coordinated_Universal_Time)
   * `YYYY-MM-DDThh:mm:ss.sssZ` format.
   */
  eventDate?: Date | null;
}

/** @oneof */
export interface SubscriptionEventEventOneOf {
  /** Triggered when a subscription is created. */
  created?: SubscriptionCreated;
  /**
   * Triggered when a subscription is assigned to a Wix site, including the initial
   * assignment of a floating subscription or a re-assignement from a different site.
   */
  assigned?: SubscriptionAssigned;
  /** Triggered when a subscription is canceled. */
  cancelled?: SubscriptionCancelled;
  /** Triggered when the subscription's auto renew is turned on. */
  autoRenewTurnedOn?: SubscriptionAutoRenewTurnedOn;
  /** Triggered when the subscription's auto renew is turned off. */
  autoRenewTurnedOff?: SubscriptionAutoRenewTurnedOff;
  /**
   * Triggered when a subscription is unassigned from a Wix site and becomes
   * floating.
   */
  unassigned?: SubscriptionUnassigned;
  /**
   * Triggered when a subscription is transferred from one Wix account to another.
   * A transfer includes cancelling the original subscription and creating a new
   * subscription for the target account. The event returns both the original
   * and the new subscription.
   */
  transferred?: SubscriptionTransferred;
  /** Triggered when a recurring charge succeeds for a subscription. */
  recurringChargeSucceeded?: RecurringChargeSucceeded;
  /**
   * Triggered when a subscription was updated including when its product has been
   * up- or downgraded or the billing cycle is changed.
   */
  contractSwitched?: ContractSwitched;
  /**
   * Triggered when a subscription gets close to the end of its billing cycle.
   * The exact number of days is defined in the billing system.
   */
  nearEndOfPeriod?: SubscriptionNearEndOfPeriod;
  /**
   * Triggered when a subscription is updated and the change doesn't happen
   * immediately but at the end of the current billing cycle.
   */
  pendingChange?: SubscriptionPendingChange;
  /** Triggered when a recurring charge attempt fails for a subscription. */
  recurringChargeAttemptFailed?: RecurringChargeAttemptFailed;
}

/** Triggered when a subscription is created. */
export interface SubscriptionCreated {
  /** Created subscription. */
  subscription?: Subscription;
  /** Metadata for the `created` event. */
  metadata?: Record<string, string>;
  /**
   * Subscription reactivation data.
   * A subscription can be reactivated for example if it was incorrectly canceled because of fraud and then reactivated
   * by the billing system
   */
  reactivationData?: ReactivationData;
}

/**
 * A subscription holds information about a Premium product that a Wix account
 * owner has purchased including details about the billing.
 */
export interface Subscription {
  /**
   * ID of the subscription.
   * @format GUID
   */
  _id?: string;
  /**
   * ID of the Wix account that purchased the subscription.
   * @format GUID
   */
  userId?: string;
  /**
   * ID of the [product](https://bo.wix.com/wix-docs/rest/premium/premium-product-catalog-v2/products/product-object)
   * for which the subscription was purchased.
   * @format GUID
   */
  productId?: string;
  /**
   * Date and time the subscription was created in
   * [UTC datetime](https://en.wikipedia.org/wiki/Coordinated_Universal_Time)
   * `YYYY-MM-DDThh:mm:ss.sssZ` format.
   */
  createdAt?: Date | null;
  /**
   * Date and time the subscription was last updated in
   * [UTC datetime](https://en.wikipedia.org/wiki/Coordinated_Universal_Time)
   * `YYYY-MM-DDThh:mm:ss.sssZ` format.
   */
  updatedAt?: Date | null;
  /**
   * ID of the metasite that the subscription is assigned to.
   * Available only when the subscription is assigned to a Wix site.
   * Subscriptions for account level products can't be assigned to a Wix site.
   * @format GUID
   */
  metaSiteId?: string | null;
  /** Information about the system that manages the subscription's billing. */
  billingReference?: BillingReference;
  /** Information about the billing cycle of the subscription. */
  cycle?: Cycle;
  /**
   * Subscription status.
   *
   * + `UNKNOWN`: Default status.
   * + `AUTO_RENEW_ON`: Subscription is active and automatically renews at the end of the current billing cycle.
   * + `AUTO_RENEW_OFF`: Subscription is active but expires at the end of the current billing cycle.
   * + `MANUAL_RECURRING`: Subscription is active and renews at the end of the current billing cycle, in case the customer takes an action related to the payment.
   * + `CANCELLED`: Subscription isn't active because it has been canceled.
   * + `TRANSFERRED`: Subscription isn't active because it has been transferred to a different account. A different active subscription was created for the target account.
   */
  status?: SubscriptionStatusWithLiterals;
  /**
   * Date and time the subscription was last transferred from one Wix account to
   * another in
   * [UTC datetime](https://en.wikipedia.org/wiki/Coordinated_Universal_Time)
   * `YYYY-MM-DDThh:mm:ss.sssZ` format.
   */
  transferredAt?: Date | null;
  /**
   * ID of the [product type](https://bo.wix.com/wix-docs/rest/premium/premium-product-catalog-v2/product-types/product-type-object)
   * that the product, for which the subscription was purchased, belongs to.
   * @format GUID
   */
  productTypeId?: string;
  /** Version number, which increments by 1 each time the subscription is updated. */
  version?: number;
  /**
   * Whether the subscription is active. Includes the statuses
   * `"AUTO_RENEW_ON"`, `"AUTO_RENEW_OFF"`, and `"MANUAL_RECURRING"`.
   */
  active?: boolean;
  /**
   * Date and time the subscription was originally created in
   * [UTC datetime](https://en.wikipedia.org/wiki/Coordinated_Universal_Time)
   * `YYYY-MM-DDThh:mm:ss.sssZ` format.
   * Differs from `createdAt` in case the subscription was originally created for a different Wix account and has been transferred.
   */
  originalCreationDate?: Date | null;
  /** Custom metadata about the subscription. */
  metadata?: Record<string, string>;
  /**
   * 2-letter country code in
   * [ISO-3166 alpha-2](https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2#Officially_assigned_code_elements)
   * format.
   */
  countryCode?: string | null;
  /**
   * Seats of the product that the subscription was purchased for.
   * Will be Null for products that don't support seats.
   * @min 1
   * @max 500
   */
  seats?: number | null;
}

export interface BillingReference {
  /**
   * Name of the billing system that manages the subscription.
   *
   * + `"UNKNOWN"`: Default value.
   * + `"SBS"`: [Wix Billing](https://github.com/wix-p/premium-billing/tree/master/sbs).
   * + `"LICENSER"`:
   * + `"BASS"`: [Billing and Subscriptions System](https://dev.wix.com/docs/rest/internal-only/premium/subscriptions-by-billing-by-wix/introduction).
   * + `"RESELLER"`: [External Reseller](https://dev.wix.com/api/rest/account-level-apis/resellers/introduction).
   */
  providerName?: ProviderNameWithLiterals;
  /** Current provider reference ID. */
  providerReferenceId?: string | null;
  /** Previous provider reference IDs. Used for when a subscription is extended, specifically for domains. */
  previousProviderReferenceIds?: string[];
}

export enum ProviderName {
  UNKNOWN = 'UNKNOWN',
  SBS = 'SBS',
  LICENSER = 'LICENSER',
  BASS = 'BASS',
  RESELLER = 'RESELLER',
  RECURRING_INVOICES = 'RECURRING_INVOICES',
}

/** @enumType */
export type ProviderNameWithLiterals =
  | ProviderName
  | 'UNKNOWN'
  | 'SBS'
  | 'LICENSER'
  | 'BASS'
  | 'RESELLER'
  | 'RECURRING_INVOICES';

export interface Cycle extends CycleCycleSelectorOneOf {
  /** repetitive interval */
  interval?: Interval;
  /** one time */
  oneTime?: OneTime;
}

/** @oneof */
export interface CycleCycleSelectorOneOf {
  /** repetitive interval */
  interval?: Interval;
  /** one time */
  oneTime?: OneTime;
}

export interface Interval {
  /** interval unit of measure */
  unit?: IntervalUnitWithLiterals;
  /** number of interval */
  count?: number;
}

export enum IntervalUnit {
  /** unknown interval unit */
  UNKNOWN = 'UNKNOWN',
  /** day */
  DAY = 'DAY',
  /** week */
  WEEK = 'WEEK',
  /** month */
  MONTH = 'MONTH',
  /** year */
  YEAR = 'YEAR',
}

/** @enumType */
export type IntervalUnitWithLiterals =
  | IntervalUnit
  | 'UNKNOWN'
  | 'DAY'
  | 'WEEK'
  | 'MONTH'
  | 'YEAR';

export interface OneTime {}

export enum SubscriptionStatus {
  UNKNOWN = 'UNKNOWN',
  AUTO_RENEW_ON = 'AUTO_RENEW_ON',
  AUTO_RENEW_OFF = 'AUTO_RENEW_OFF',
  MANUAL_RECURRING = 'MANUAL_RECURRING',
  CANCELLED = 'CANCELLED',
  TRANSFERRED = 'TRANSFERRED',
}

/** @enumType */
export type SubscriptionStatusWithLiterals =
  | SubscriptionStatus
  | 'UNKNOWN'
  | 'AUTO_RENEW_ON'
  | 'AUTO_RENEW_OFF'
  | 'MANUAL_RECURRING'
  | 'CANCELLED'
  | 'TRANSFERRED';

/** Triggered when a subscription is reactivated. */
export interface ReactivationData {
  reactivationReason?: ReactivationReasonEnumWithLiterals;
  /**
   * In the event of reactivation after chargeback dispute, the subscription may be extended according to the
   * number of days it was inactive during the time of resolving the dispute
   */
  newEndOfPeriod?: Date | null;
  /** The original end date, before the inactive period. */
  oldEndOfPeriod?: Date | null;
  /** The difference in days between the new new_end_of_period and old_end_of_period */
  differenceInDays?: number | null;
}

/** Reason for subscription reactivation */
export enum ReactivationReasonEnum {
  UNKNOWN = 'UNKNOWN',
  /**
   * Subscription was reactivated due to billing status change from CANCELED to ACTIVE, for example if it was incorrectly
   * canceled because of suspicion of fraud
   */
  BILLING_STATUS_CHANGE = 'BILLING_STATUS_CHANGE',
  /** Subscription was reactivated after a chargeback dispute */
  REACTIVATED_AFTER_CHARGEBACK = 'REACTIVATED_AFTER_CHARGEBACK',
}

/** @enumType */
export type ReactivationReasonEnumWithLiterals =
  | ReactivationReasonEnum
  | 'UNKNOWN'
  | 'BILLING_STATUS_CHANGE'
  | 'REACTIVATED_AFTER_CHARGEBACK';

/**
 * Triggered when a subscription is assigned to a Wix site, including the initial
 * assignment of a floating subscription or a re-assignement from a different site.
 */
export interface SubscriptionAssigned {
  /** Assigned subscription. */
  subscription?: Subscription;
  /**
   * ID of the metasite that the subscription has been assigned to before the update.
   * @format GUID
   */
  previousMetaSiteId?: string | null;
}

/** Triggered when a subscription is canceled. */
export interface SubscriptionCancelled {
  /** Canceled subscription. */
  subscription?: Subscription;
  /** Details about the cancellation including who canceled the subscription and why. */
  cancellationDetails?: CancellationDetails;
  /**
   * Whether the subscription is canceled immediately or expires at the end of the current billing cycle.
   *
   * Default: `false`
   */
  immediateCancel?: boolean;
  /** Whether the subscription was canceled during the free trial period. */
  canceledInFreeTrial?: boolean;
  /** The type of refund applied to the cancellation. */
  refundType?: RefundTypeWithLiterals;
}

/** Information about the cancellation flow including who canceled the subscription and why it was canceled. */
export interface CancellationDetails {
  /**
   * Cancellation code.
   *
   * Values supported for cancellations on behalf of the billing system: `-1`, `-2`, `-3`, `-4`, `-5`, `-6`, `-7`, `-8`.
   * For cancellations on behalf of the site owner or the service provider `cancellationCode`
   * is taken from the request of
   * [Cancel Immediately Offline](https://bo.wix.com/wix-docs/rest/premium/premium-subscriptions-manager/cancel-immediately-offline).
   *
   * + `-1`: The subscription has been cancelled by the billing system but none of the listed cancellation reasons applies.
   * + `-2`: There were payment problems.
   * + `-3`: There was a chargeback.
   * + `-4`: Customer support has canceled the subscription and issued a refund.
   * + `-5`: The site owner has changed their existing subscription.
   * + `-6`: The subscription has been transferred to a different Wix account.
   * + `-7`: The subscription has been canceled because the site owner hasn't manually authenticated the recurring payment during the subscription's grace period. For example, site owners must manually confirm recurring payments within 40 days when paying with boleto.
   * + `-8`: The Wix account that the subscription belonged to has been deleted.
   */
  cancellationCode?: number | null;
  /**
   * Cancellation reason. For cancellations on behalf of the site owner or the service provider `cancellationReason`
   * is taken from the request of
   * [Cancel Immediately Offline](https://bo.wix.com/wix-docs/rest/premium/premium-subscriptions-manager/cancel-immediately-offline).
   * For cancellations on behalf of the billing system `cancellationReason` is `null` or an empty string.
   */
  cancellationReason?: string | null;
  /**
   * Initiator of the cancellation. For `"USER_REQUESTED"` and `"APP_MANAGED"`,
   * `cancellationCode` and `cancellationReason` are taken from the request of
   * [Cancel Immediately](https://dev.wix.com/docs/rest/account-level/premium/premium-subscriptions-manager/subscription-v1/cancel-immediately)
   * or [Cancel Immediately Offline](https://dev.wix.com/docs/rest/account-level/premium/premium-subscriptions-manager/subscription-v1/cancel-immediately-offline).
   * For `"PASSIVE"`, cancellations `cancellationCode` is automatically calculated and `cancellationReason`
   * is `null` or an empty string.
   *
   * + `"UNKNOWN`: Default value.
   * + `"USER_REQUESTED"`:  The Wix account owner has canceled the subscription.
   * + `"APP_MANAGED"`: The service provider has canceled the subscription.
   * + `"PASSIVE"`: The billing system has canceled the subscription. For example, in case of payment failure or fraud.
   */
  initiator?: InitiatorWithLiterals;
}

export enum Initiator {
  UNKNOWN = 'UNKNOWN',
  USER_REQUESTED = 'USER_REQUESTED',
  APP_MANAGED = 'APP_MANAGED',
  PASSIVE = 'PASSIVE',
}

/** @enumType */
export type InitiatorWithLiterals =
  | Initiator
  | 'UNKNOWN'
  | 'USER_REQUESTED'
  | 'APP_MANAGED'
  | 'PASSIVE';

export enum RefundType {
  UNKNOWN = 'UNKNOWN',
  NO_REFUND = 'NO_REFUND',
  FULL_REFUND = 'FULL_REFUND',
  PRORATED_REFUND = 'PRORATED_REFUND',
}

/** @enumType */
export type RefundTypeWithLiterals =
  | RefundType
  | 'UNKNOWN'
  | 'NO_REFUND'
  | 'FULL_REFUND'
  | 'PRORATED_REFUND';

/** Triggered when the subscription's auto renew is turned on. */
export interface SubscriptionAutoRenewTurnedOn {
  /** Subscription for which auto renew is turned on. */
  subscription?: Subscription;
  /**
   * Supported values: `USER`, `APP`.
   *
   * Information about who turned auto renew on.
   * + `"USER"`: The site owner who purchased the subscription has turned auto renew on.
   * + `"APP"`: The service provider has turned auto renew on.
   */
  initiator?: string | null;
}

/** Triggered when the subscription's auto renew is turned off. */
export interface SubscriptionAutoRenewTurnedOff {
  /** Subscription for which auto renew is turned off. */
  subscription?: Subscription;
  /** Details about the cancellation including who canceled the subscription and why. */
  cancellationDetails?: CancellationDetails;
  /**
   * Whether the subscription is immediately canceled or expires at the end of the current billing cycle.
   *
   * Default: `false`
   */
  immediateCancel?: boolean;
}

/**
 * Triggered when a subscription is unassigned from a Wix site and becomes
 * floating.
 */
export interface SubscriptionUnassigned {
  /** Unassigned subscription. */
  subscription?: Subscription;
  /**
   * ID of the metasite that the subscription has been assigned to before the event.
   * @format GUID
   */
  previousMetaSiteId?: string;
  /**
   * Reason why the subscription is unassigned.
   *
   * + `"UNKNOWN"`: Default value.
   * + `"USER_REQUESTED"`: The Wix account owner has unassigned the subscription.
   * + `"REPLACED_BY_ANOTHER_SUBSCRIPTION"`: A different subscription that replaces this subscription is assigned to the site.
   */
  unassignReason?: UnassignReasonWithLiterals;
}

export enum UnassignReason {
  UNKNOWN = 'UNKNOWN',
  USER_REQUESTED = 'USER_REQUESTED',
  REPLACED_BY_ANOTHER_SUBSCRIPTION = 'REPLACED_BY_ANOTHER_SUBSCRIPTION',
}

/** @enumType */
export type UnassignReasonWithLiterals =
  | UnassignReason
  | 'UNKNOWN'
  | 'USER_REQUESTED'
  | 'REPLACED_BY_ANOTHER_SUBSCRIPTION';

/**
 * Triggered when a subscription is transferred from one Wix account to another.
 * A transfer includes cancelling the original subscription and creating a new
 * subscription for the target account. The event returns both the original
 * and the new subscription.
 */
export interface SubscriptionTransferred {
  /** Original subscription that was canceled for the transfer. */
  originSubscription?: Subscription;
  /** Newly created subscription for the target account. */
  targetSubscription?: Subscription;
}

/** Triggered when a recurring charge succeeds for a subscription. */
export interface RecurringChargeSucceeded {
  /** Subscription for which the recurring charge has succeeded. */
  subscription?: Subscription;
  /** Indication that there was a successful charge at the end of the free trial period */
  freeTrialPeriodEnd?: boolean;
}

/**
 * Triggered when a subscription was updated including when its product has been
 * up- or downgraded or the billing cycle is changed.
 */
export interface ContractSwitched {
  /** Updated subscription. */
  subscription?: Subscription;
  /** Billing cycle before the update. */
  previousCycle?: Cycle;
  /**
   * ID of the product belonging to the subscription before the update.
   * @format GUID
   */
  previousProductId?: string;
  /**
   * ID of the product type that the subscription's original product belonged to before the update.
   * @format GUID
   */
  previousProductTypeId?: string;
  /**
   * Update type. __Note__: Doesn't include information about a product adjustment.
   * For that purpose, see `productAdjustment`.
   *
   * + `"NOT_APPLICABLE"`: Default value.
   * + `"ADDITIONAL_QUANTITY"`: An increased usage quota is added to the subscription. For example, a second mailbox is added to a subscription that previously included a single mailbox.
   * + `"CREDIT_UNUSED_PERIOD"`: The subscription is upgraded and the new price is less than the regular price. The new price applies to every billing cycle, not just the first cycle.
   * + `"REFUND_PRICE_DIFF"`: Not implemented.
   * + `"ADJUST_PERIOD_END"`: Not implemented.
   * + `"DOWNGRADE_GRACE_PERIOD"`: For downgrades during the grace period. In this situation, the site owner hasn’t paid yet and must immediately pay for the downgraded subscription.
   * + `"FULL_AMOUNT_PERIOD"`: For upgrades in which the site owner retains unused benefits. For example, site owners upgrading a Facebook Ads subscription retain their unused FB Ads credit. The unused credit is added to the new credit.
   * + `"END_OF_PERIOD"`: The subscription's billing current cycle is extended because of a downgrade.
   * + `"PENDING_CHANGES"`: The subscription's billing is updated, but the change doesn't apply immediately. Instead, the update becomes effective at the end of current billing cycle.
   * + `"DOWNGRADE_RENEWAL"`: The subscription is downgraded because of a declined payment. This prevents subscriptions from churning.
   */
  contractSwitchType?: ContractSwitchTypeWithLiterals;
  /**
   * ID of the metasite the subscription has been assigned to previously.
   * Available only in case the subscription is assigned to a different site.
   * @format GUID
   */
  previousMetaSiteId?: string | null;
  /**
   * Update reason.
   *
   * + `"PRICE_INCREASE"`: The subscription's price has been increased.
   * + `"EXTERNAL_PROVIDER_TRIGGER"`: Any reason other than a price increase.
   */
  contractSwitchReason?: ContractSwitchReasonWithLiterals;
  /** Information about the price update. Available only for updates with a price increase. */
  productPriceIncreaseData?: ProductPriceIncreaseData;
  /**
   * Information about a product adjustment. For example, a downgrade.
   * __Note__: This isn't the same as `contractSwitchType`.
   *
   * + `NOT_APPLICABLE`: There is no information about whether the product has been up- or downgraded.
   * + `DOWNGRADE`: The product has been downgraded.
   */
  productAdjustment?: ProductAdjustmentWithLiterals;
  /**
   * Number of seats before the contract switch.
   * @min 1
   * @max 500
   */
  previousSeats?: number | null;
}

/** Copied from SBS */
export enum ContractSwitchType {
  NOT_APPLICABLE = 'NOT_APPLICABLE',
  ADDITIONAL_QUANTITY = 'ADDITIONAL_QUANTITY',
  CREDIT_UNUSED_PERIOD = 'CREDIT_UNUSED_PERIOD',
  REFUND_PRICE_DIFF = 'REFUND_PRICE_DIFF',
  ADJUST_PERIOD_END = 'ADJUST_PERIOD_END',
  DOWNGRADE_GRACE_PERIOD = 'DOWNGRADE_GRACE_PERIOD',
  FULL_AMOUNT_PERIOD = 'FULL_AMOUNT_PERIOD',
  END_OF_PERIOD = 'END_OF_PERIOD',
  PENDING_CHANGES = 'PENDING_CHANGES',
  DOWNGRADE_RENEWAL = 'DOWNGRADE_RENEWAL',
  FIXED_BILLING_DATE_PRORATION = 'FIXED_BILLING_DATE_PRORATION',
  IMMEDIATE_SWITCH = 'IMMEDIATE_SWITCH',
}

/** @enumType */
export type ContractSwitchTypeWithLiterals =
  | ContractSwitchType
  | 'NOT_APPLICABLE'
  | 'ADDITIONAL_QUANTITY'
  | 'CREDIT_UNUSED_PERIOD'
  | 'REFUND_PRICE_DIFF'
  | 'ADJUST_PERIOD_END'
  | 'DOWNGRADE_GRACE_PERIOD'
  | 'FULL_AMOUNT_PERIOD'
  | 'END_OF_PERIOD'
  | 'PENDING_CHANGES'
  | 'DOWNGRADE_RENEWAL'
  | 'FIXED_BILLING_DATE_PRORATION'
  | 'IMMEDIATE_SWITCH';

export enum ContractSwitchReason {
  EXTERNAL_PROVIDER_TRIGGER = 'EXTERNAL_PROVIDER_TRIGGER',
  PRICE_INCREASE = 'PRICE_INCREASE',
}

/** @enumType */
export type ContractSwitchReasonWithLiterals =
  | ContractSwitchReason
  | 'EXTERNAL_PROVIDER_TRIGGER'
  | 'PRICE_INCREASE';

/** Triggered when a subscription's price is increased. */
export interface ProductPriceIncreaseData {
  /**
   * Price of the subscription before the update.
   * @format DECIMAL_VALUE
   */
  previousPrice?: string | null;
  /** A value that is used in order to select the correct email template to send the user regarding the price increase. */
  emailTemplateSelector?: string | null;
  /** Used to differentiate between migration segments. Does not have to be unique per segment. */
  segmentName?: string | null;
  /** Used to determine how the price increase was triggered. */
  priceIncreaseTrigger?: PriceIncreaseTriggerWithLiterals;
}

/** Reason for Price Increase Trigger */
export enum PriceIncreaseTrigger {
  NEAR_RENEWAL = 'NEAR_RENEWAL',
  RECURRING_SUCCESS = 'RECURRING_SUCCESS',
  MANUAL = 'MANUAL',
}

/** @enumType */
export type PriceIncreaseTriggerWithLiterals =
  | PriceIncreaseTrigger
  | 'NEAR_RENEWAL'
  | 'RECURRING_SUCCESS'
  | 'MANUAL';

/** Triggered when a subscription's product is adusted. */
export enum ProductAdjustment {
  /** flag to show that the ContractSwitchedEvent is not applicable / needed */
  NOT_APPLICABLE = 'NOT_APPLICABLE',
  /** flag to show that the ContractSwitchedEvent is a Downgrade */
  DOWNGRADE = 'DOWNGRADE',
}

/** @enumType */
export type ProductAdjustmentWithLiterals =
  | ProductAdjustment
  | 'NOT_APPLICABLE'
  | 'DOWNGRADE';

/**
 * Triggered when a subscription gets close to the end of its billing cycle.
 * The exact number of days is defined in the billing system.
 */
export interface SubscriptionNearEndOfPeriod {
  /** Subscription that got close to the end of its billing cycle. */
  subscription?: Subscription;
  /** Whether the subscription is within the free trial period. */
  inFreeTrial?: boolean;
}

/**
 * Triggered when a subscription is updated and the change doesn't happen
 * immediately but at the end of the current billing cycle.
 */
export interface SubscriptionPendingChange {
  /** Subscription for which a pending update is triggered. */
  subscription?: Subscription;
}

/** Triggered when a recurring charge attempt failed for a subscription. */
export interface RecurringChargeAttemptFailed {
  /** Subscription for which the recurring charge attempt has failed. */
  subscription?: Subscription;
}

export interface Empty {}

export interface ProductInstanceUpdated {
  /** Updated package. */
  package?: Package;
  /**
   * ID of the product instance that was adjusted.
   * @readonly
   * @format GUID
   */
  productInstanceId?: string;
}

export interface FailureReportRequest {
  /**
   * ID of the product instance that failed.
   * @format GUID
   */
  instanceId?: string;
  /** Failure reason. */
  reason?: FailureReason;
}

export interface ProductInstanceFailed {
  /** Updated package. */
  package?: Package;
  /**
   * ID of the product instance that `FAILED`.
   * @readonly
   * @format GUID
   */
  productInstanceId?: string;
  /** Failure reason. */
  reason?: FailureReason;
}

export interface UpdateProductInstanceRequest {
  /**
   * ID of the product instance to update.
   * @format GUID
   */
  instanceId?: string;
  status?: StatusWithLiterals;
  /** @format COUNTRY */
  countryCode?: string | null;
  /** Which field to update, currently only status and countryCode are supported. */
  fieldToUpdate?: FieldToUpdateWithLiterals;
}

export enum FieldToUpdate {
  STATUS = 'STATUS',
  COUNTRY_CODE = 'COUNTRY_CODE',
}

/** @enumType */
export type FieldToUpdateWithLiterals =
  | FieldToUpdate
  | 'STATUS'
  | 'COUNTRY_CODE';

export interface UpdateProductInstanceResponse {
  /** Updated Product Instance */
  productInstance?: ProductInstance;
}

export interface UpdatePackageExternalIdRequest {
  /**
   * ID of the package to update.
   * @format GUID
   */
  packageId: string;
  /**
   * External ID that will be assigned to the package.
   * @maxLength 100
   */
  externalId: string;
}

export interface UpdatePackageExternalIdResponse {
  /** Updated package. */
  updatedPackage?: Package;
}

export interface CountPackagesRequest {
  /** Filter on what packages to count */
  filter?: Record<string, any> | null;
}

export interface CountPackagesResponse {
  /** Number of packages */
  count?: number;
}

export interface DomainEvent extends DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  _id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at 16:00 and then again at 16:01, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
}

/** @oneof */
export interface DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
}

export interface EntityCreatedEvent {
  entity?: string;
}

export interface RestoreInfo {
  deletedDate?: Date | null;
}

export interface EntityUpdatedEvent {
  /**
   * Since platformized APIs only expose PATCH and not PUT we can't assume that the fields sent from the client are the actual diff.
   * This means that to generate a list of changed fields (as opposed to sent fields) one needs to traverse both objects.
   * We don't want to impose this on all developers and so we leave this traversal to the notification recipients which need it.
   */
  currentEntity?: string;
}

export interface EntityDeletedEvent {
  /** Entity that was deleted. */
  deletedEntity?: string | null;
}

export interface ActionEvent {
  body?: string;
}

export interface MessageEnvelope {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Stringify payload. */
  data?: string;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface IdentificationData extends IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
  /** @readonly */
  identityType?: WebhookIdentityTypeWithLiterals;
}

/** @oneof */
export interface IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
}

export enum WebhookIdentityType {
  UNKNOWN = 'UNKNOWN',
  ANONYMOUS_VISITOR = 'ANONYMOUS_VISITOR',
  MEMBER = 'MEMBER',
  WIX_USER = 'WIX_USER',
  APP = 'APP',
}

/** @enumType */
export type WebhookIdentityTypeWithLiterals =
  | WebhookIdentityType
  | 'UNKNOWN'
  | 'ANONYMOUS_VISITOR'
  | 'MEMBER'
  | 'WIX_USER'
  | 'APP';

export interface AccountInfo {
  /**
   * ID of the Wix account associated with the event.
   * @format GUID
   */
  accountId?: string | null;
  /**
   * ID of the parent Wix account. Only included when accountId belongs to a child account.
   * @format GUID
   */
  parentAccountId?: string | null;
  /**
   * ID of the Wix site associated with the event. Only included when the event is tied to a specific site.
   * @format GUID
   */
  siteId?: string | null;
}

export interface MigrateSubscriptionToPackagesRequest {
  /** Created package. */
  productInstance?: ProductInstance;
  /**
   * Existing subscriptionId to migrate
   * @format GUID
   */
  subscriptionId?: string | null;
  /**
   * User id of the migration - only for Immigrator
   * @format GUID
   */
  userId?: string | null;
}

export interface MigrateSubscriptionToPackagesResponse {
  package?: Package;
}

export interface FixBrokenMigrationRequest {
  /**
   * Subscription id of the broken subscription
   * @format GUID
   */
  subscriptionId?: string;
  /**
   * SBS service id that points to the broken subscription
   * @format GUID
   */
  sbsServiceId?: string;
  /**
   * Owner of the broken subscription
   * @format GUID
   */
  userId?: string;
}

export interface FixBrokenMigrationResponse {}

/**
 * Creates a new package.
 *
 * Deprecated: This method is deprecated and will be removed by 2024-12-31.
 * Use com.wixpress.premium.reseller.packages.v1.Packages.CreatePackageV2 instead.
 *
 * HTTP Request:
 * POST /v1/packages
 *
 * Permissions:
 * Requires the "premium.reseller-package.manage" permission with MANUAL type.
 *
 * Event:
 * A PRIVATE callback will be triggered when the package is successfully created.
 *
 * Required Fields:
 * - CreatePackageRequest.products
 *
 * Maturity: GA
 * Exposure: PUBLIC
 * @public
 * @requiredField options.products
 * @permissionId premium.reseller-package.manage
 * @fqn com.wixpress.premium.reseller.packages.v1.Packages.CreatePackage
 * @deprecated
 * @replacedBy com.wixpress.premium.reseller.packages.v1.Packages.CreatePackageV2
 * @targetRemovalDate 2024-12-31
 */
export async function createPackage(
  options?: NonNullablePaths<CreatePackageOptions, `products`, 2>
): Promise<
  NonNullablePaths<
    CreatePackageResponse,
    | `package._id`
    | `package.accountId`
    | `package.productInstances`
    | `package.productInstances.${number}.billingInfo.type`
    | `package.productInstances.${number}.billingInfo.cycleDuration.unit`
    | `package.productInstances.${number}.billingInfo.cycleDuration.count`
    | `package.productInstances.${number}.instanceId`
    | `package.productInstances.${number}.catalogProductId`
    | `package.productInstances.${number}.status`
    | `package.productInstances.${number}.failure.code`
    | `package.productInstances.${number}.failure.message`,
    7
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    idempotencyKey: options?.idempotencyKey,
    externalId: options?.externalId,
    products: options?.products,
  });

  const reqOpts = ambassadorWixPremiumResellerV1Packages.createPackage(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          idempotencyKey: '$[0].idempotencyKey',
          externalId: '$[0].externalId',
          products: '$[0].products',
        },
        singleArgumentUnchanged: false,
      },
      ['options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface CreatePackageOptions {
  /**
   * Idempotency key.
   * @maxLength 100
   */
  idempotencyKey?: string | null;
  /**
   * External reference. For example, an external subscription ID. **Note:** This field is not validated by Wix.
   * @maxLength 100
   */
  externalId?: string | null;
  /**
   * Wix services that are resold.
   * @maxSize 1000
   * @minSize 1
   */
  products: ProductInstance[];
}

/**
 * Creates a package of product instances.
 *
 *
 * You must pass the relevant Wix account ID in the header of the call. In
 * the DIY flow, we recommend to pass the customer's sub-account ID instead
 * of your main reseller account ID.
 *
 * You may also pass a Wix site ID for each product in the body of the call.
 * If you omit the site ID, a floating product instance is created.
 *
 * When Wix customers purchase a specific paid service or product, Wix may offer
 * them time-limited free access to a different product. For example, customers
 * get a voucher for a free 1-year domain registration when purchasing any Wix
 * Premium plan. If you want to offer your customers the same benefit, create a
 * package containing the original product first. Then, create a second package
 * with the additional product. In the second Create Package call, pass the
 * instance ID of the original product as `referenceProductInstanceId`. This way,
 * Wix doesn't charge you for the additional product. Make sure that the status
 * of the referenced product is either `"PENDING"` or `"ACTIVE"`. Note that you
 * can use each product instance only a single time as reference instance.
 *
 * You need to pass a `countryCode` to specify where customers can claim
 * vouchers that may come with a product. Contact the
 * [Wix B2B sales team](mailto:bizdev@wix.com) to get information about
 * vouchers and supported locations.
 *
 * > **Important**: This call requires an account level API key and cannot be
 * > authenticated with the standard authorization header.
 * @public
 * @requiredField options.products
 * @requiredField options.products.countryCode
 * @permissionId premium.reseller-package.manage
 * @fqn com.wixpress.premium.reseller.packages.v1.Packages.CreatePackageV2
 */
export async function createPackageV2(
  options?: NonNullablePaths<
    CreatePackageV2Options,
    `products` | `products.${number}.countryCode`,
    4
  >
): Promise<
  NonNullablePaths<
    CreatePackageResponse,
    | `package._id`
    | `package.accountId`
    | `package.productInstances`
    | `package.productInstances.${number}.billingInfo.type`
    | `package.productInstances.${number}.billingInfo.cycleDuration.unit`
    | `package.productInstances.${number}.billingInfo.cycleDuration.count`
    | `package.productInstances.${number}.instanceId`
    | `package.productInstances.${number}.catalogProductId`
    | `package.productInstances.${number}.status`
    | `package.productInstances.${number}.failure.code`
    | `package.productInstances.${number}.failure.message`,
    7
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    idempotencyKey: options?.idempotencyKey,
    externalId: options?.externalId,
    products: options?.products,
  });

  const reqOpts =
    ambassadorWixPremiumResellerV1Packages.createPackageV2(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          idempotencyKey: '$[0].idempotencyKey',
          externalId: '$[0].externalId',
          products: '$[0].products',
        },
        singleArgumentUnchanged: false,
      },
      ['options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface CreatePackageV2Options {
  /**
   * Idempotency key.
   * @maxLength 100
   */
  idempotencyKey?: string | null;
  /**
   * External reference. For example, an external subscription ID. **Note:** This field is not validated by Wix.
   * @maxLength 100
   */
  externalId?: string | null;
  /**
   * Wix services that are resold.
   * @maxSize 1000
   * @minSize 1
   */
  products: ProductInstance[];
}

/**
 * Cancels all product instances included in the package and the customer
 * immediately loses access to the canceled functionality.
 *
 *
 * You must pass the ID of the Wix account that the package belongs to in the
 * header of the call. The call fails, if the package and Wix account don't
 * match.
 *
 * If a canceled product instance is a requirement for another Wix service, that
 * functionality is also no longer available to the customer. For example, if
 * you cancel a Premium plan, a previously connected domain is automatically
 * disconnected from the site.
 *
 * > **Important**: This call requires an account level API key and cannot be
 * > authenticated with the standard authorization header.
 * @param _id - ID of the package to cancel.
 * @public
 * @requiredField _id
 * @permissionId premium.reseller-package.manage
 * @fqn com.wixpress.premium.reseller.packages.v1.Packages.CancelPackage
 */
export async function cancelPackage(
  _id: string,
  options?: CancelPackageOptions
): Promise<
  NonNullablePaths<
    CancelPackageResponse,
    | `package._id`
    | `package.accountId`
    | `package.productInstances`
    | `package.productInstances.${number}.billingInfo.type`
    | `package.productInstances.${number}.billingInfo.cycleDuration.unit`
    | `package.productInstances.${number}.billingInfo.cycleDuration.count`
    | `package.productInstances.${number}.instanceId`
    | `package.productInstances.${number}.catalogProductId`
    | `package.productInstances.${number}.status`
    | `package.productInstances.${number}.failure.code`
    | `package.productInstances.${number}.failure.message`,
    7
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    id: _id,
    idempotencyKey: options?.idempotencyKey,
  });

  const reqOpts = ambassadorWixPremiumResellerV1Packages.cancelPackage(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          id: '$[0]',
          idempotencyKey: '$[1].idempotencyKey',
        },
        singleArgumentUnchanged: false,
      },
      ['_id', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface CancelPackageOptions {
  /**
   * Idempotency key.
   * @maxLength 100
   */
  idempotencyKey?: string | null;
}

/**
 * Retrieves a package.
 *
 *
 * You must pass the ID of the Wix account that the package belongs to in the
 * header of the call. The call fails, if the package and Wix account don't
 * match.
 *
 * > **Important**: This call requires an account level API key and cannot be
 * > authenticated with the standard authorization header.
 * @param _id - Package ID.
 * @public
 * @requiredField _id
 * @permissionId premium.reseller-package.read
 * @returns Retrieved package.
 * @fqn com.wixpress.premium.reseller.packages.v1.Packages.GetPackage
 */
export async function getPackage(
  _id: string
): Promise<
  NonNullablePaths<
    Package,
    | `_id`
    | `accountId`
    | `productInstances`
    | `productInstances.${number}.billingInfo.type`
    | `productInstances.${number}.billingInfo.cycleDuration.unit`
    | `productInstances.${number}.billingInfo.cycleDuration.count`
    | `productInstances.${number}.instanceId`
    | `productInstances.${number}.catalogProductId`
    | `productInstances.${number}.status`
    | `productInstances.${number}.failure.code`
    | `productInstances.${number}.failure.message`,
    6
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({ id: _id });

  const reqOpts = ambassadorWixPremiumResellerV1Packages.getPackage(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)?.package!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { id: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['_id']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Retrieves a list of packages, given the provided paging, filtering, and sorting.
 *
 *
 * You must pass the ID of the Wix account that the packages belong to in the
 * header of the call. The call returns packages that belong to the parent
 * account, regardless if you pass the ID of a parent or sub-account. In case
 * you want to retrieve only packages that belong to a sub-account, you must
 * pass the sub-account ID as `filter` in the `query` object like this:
 *
 * ```json
 * {
 * "query": {
 * "filter": {
 * "accountId": {
 * "$eq": "<SUB_ACCOUNT_ID>"
 * }
 * }
 * }
 * }
 * ```
 *
 * Query Packages runs with these defaults, which you can override:
 *
 * - `createdDate` is sorted in `DESC` order
 * - `cursorPaging.limit` is `100`
 *
 * By default `pagingMetadata.cursors` are returned, unless you specifically pass
 * `query.paging`.
 *
 * The maximum for `cursorPaging.limit` is `100`.
 *
 * If you pass a cursor token that you have received in a previous Query Package response,
 * passing additional sort or filter information results in an invalid cursor
 * error. Since the received cursor token already holds all the needed information
 * for sort and filter. Changing the sort and filter properties during the span of
 * a cursor query isn't supported. But you can provide a different limit.
 *
 * To learn about working with _Query_ endpoints, see
 * [API Query Language](https://dev.wix.com/api/rest/getting-started/api-query-language),
 * [Sorting and Paging](https://dev.wix.com/api/rest/getting-started/pagination),
 * and [Field Projection](https://dev.wix.com/api/rest/getting-started/field-projection).
 *
 * > **Important**: This call requires an account level API key and cannot be
 * > authenticated with the standard authorization header.
 * @public
 * @permissionId premium.reseller-package.read
 * @fqn com.wixpress.premium.reseller.packages.v1.Packages.QueryPackages
 */
export function queryPackages(): PackagesQueryBuilder {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[0] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  return queryBuilder<
    Package,
    'CURSOR',
    QueryPackagesRequest,
    QueryPackagesResponse
  >({
    func: async (payload: QueryPackagesRequest) => {
      const reqOpts =
        ambassadorWixPremiumResellerV1Packages.queryPackages(payload);

      sideEffects?.onSiteCall?.();
      try {
        const result = await httpClient.request(reqOpts);
        sideEffects?.onSuccess?.(result);
        return result;
      } catch (err) {
        sideEffects?.onError?.(err);
        throw err;
      }
    },
    requestTransformer: (query: QueryPackagesRequest['query']) => {
      const args = [query, {}] as [QueryPackagesRequest['query'], {}];
      return renameKeysFromSDKRequestToRESTRequest({
        ...args?.[1],
        query: args?.[0],
      });
    },
    responseTransformer: ({ data }: HttpResponse<QueryPackagesResponse>) => {
      const transformedData = renameKeysFromRESTResponseToSDKResponse(
        transformPaths(data, [])
      );

      return {
        items: transformedData?.packages,
        pagingMetadata: transformedData?.metadata,
      };
    },
    errorTransformer: (err: unknown) => {
      const transformedError = sdkTransformError(err, {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { query: '$[0]' },
        singleArgumentUnchanged: false,
      });

      throw transformedError;
    },
    pagingMethod: 'CURSOR',
    transformationPaths: {},
  });
}

interface QueryCursorResult {
  cursors: Cursors;
  hasNext: () => boolean;
  hasPrev: () => boolean;
  length: number;
  pageSize: number;
}

export interface PackagesQueryResult extends QueryCursorResult {
  items: Package[];
  query: PackagesQueryBuilder;
  next: () => Promise<PackagesQueryResult>;
  prev: () => Promise<PackagesQueryResult>;
}

export interface PackagesQueryBuilder {
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  eq: (
    propertyName:
      | '_id'
      | 'accountId'
      | 'externalId'
      | 'productInstances.billingInfo'
      | 'productInstances.billingInfo.type'
      | 'productInstances.billingInfo.cycleDuration'
      | 'productInstances.billingInfo.cycleDuration.unit'
      | 'productInstances.billingInfo.cycleDuration.count'
      | 'productInstances.instanceId'
      | 'productInstances.siteId'
      | 'productInstances.catalogProductId'
      | 'productInstances.status'
      | 'productInstances.failure'
      | 'productInstances.failure.code'
      | 'productInstances.failure.message'
      | 'productInstances.createdDate'
      | 'productInstances.updatedDate'
      | 'productInstances.countryCode'
      | '_createdDate'
      | '_updatedDate',
    value: any
  ) => PackagesQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  ne: (
    propertyName:
      | '_id'
      | 'accountId'
      | 'externalId'
      | 'productInstances.billingInfo'
      | 'productInstances.billingInfo.type'
      | 'productInstances.billingInfo.cycleDuration'
      | 'productInstances.billingInfo.cycleDuration.unit'
      | 'productInstances.billingInfo.cycleDuration.count'
      | 'productInstances.instanceId'
      | 'productInstances.siteId'
      | 'productInstances.catalogProductId'
      | 'productInstances.status'
      | 'productInstances.failure'
      | 'productInstances.failure.code'
      | 'productInstances.failure.message'
      | 'productInstances.createdDate'
      | 'productInstances.updatedDate'
      | 'productInstances.countryCode'
      | '_createdDate'
      | '_updatedDate',
    value: any
  ) => PackagesQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  ge: (
    propertyName:
      | '_id'
      | 'accountId'
      | 'externalId'
      | 'productInstances.billingInfo.cycleDuration.count'
      | 'productInstances.instanceId'
      | 'productInstances.siteId'
      | 'productInstances.catalogProductId'
      | 'productInstances.failure.message'
      | 'productInstances.createdDate'
      | 'productInstances.updatedDate'
      | 'productInstances.countryCode'
      | '_createdDate'
      | '_updatedDate',
    value: any
  ) => PackagesQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  gt: (
    propertyName:
      | '_id'
      | 'accountId'
      | 'externalId'
      | 'productInstances.billingInfo.cycleDuration.count'
      | 'productInstances.instanceId'
      | 'productInstances.siteId'
      | 'productInstances.catalogProductId'
      | 'productInstances.failure.message'
      | 'productInstances.createdDate'
      | 'productInstances.updatedDate'
      | 'productInstances.countryCode'
      | '_createdDate'
      | '_updatedDate',
    value: any
  ) => PackagesQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  le: (
    propertyName:
      | '_id'
      | 'accountId'
      | 'externalId'
      | 'productInstances.billingInfo.cycleDuration.count'
      | 'productInstances.instanceId'
      | 'productInstances.siteId'
      | 'productInstances.catalogProductId'
      | 'productInstances.failure.message'
      | 'productInstances.createdDate'
      | 'productInstances.updatedDate'
      | 'productInstances.countryCode'
      | '_createdDate'
      | '_updatedDate',
    value: any
  ) => PackagesQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  lt: (
    propertyName:
      | '_id'
      | 'accountId'
      | 'externalId'
      | 'productInstances.billingInfo.cycleDuration.count'
      | 'productInstances.instanceId'
      | 'productInstances.siteId'
      | 'productInstances.catalogProductId'
      | 'productInstances.failure.message'
      | 'productInstances.createdDate'
      | 'productInstances.updatedDate'
      | 'productInstances.countryCode'
      | '_createdDate'
      | '_updatedDate',
    value: any
  ) => PackagesQueryBuilder;
  /** @param propertyName - Property whose value is compared with `string`.
   * @param string - String to compare against. Case-insensitive.
   */
  startsWith: (
    propertyName:
      | '_id'
      | 'accountId'
      | 'externalId'
      | 'productInstances.instanceId'
      | 'productInstances.siteId'
      | 'productInstances.catalogProductId'
      | 'productInstances.failure.message'
      | 'productInstances.countryCode',
    value: string
  ) => PackagesQueryBuilder;
  /** @param propertyName - Property whose value is compared with `values`.
   * @param values - List of values to compare against.
   */
  hasSome: (
    propertyName:
      | '_id'
      | 'accountId'
      | 'externalId'
      | 'productInstances.billingInfo'
      | 'productInstances.billingInfo.type'
      | 'productInstances.billingInfo.cycleDuration'
      | 'productInstances.billingInfo.cycleDuration.unit'
      | 'productInstances.billingInfo.cycleDuration.count'
      | 'productInstances.instanceId'
      | 'productInstances.siteId'
      | 'productInstances.catalogProductId'
      | 'productInstances.status'
      | 'productInstances.failure'
      | 'productInstances.failure.code'
      | 'productInstances.failure.message'
      | 'productInstances.createdDate'
      | 'productInstances.updatedDate'
      | 'productInstances.countryCode'
      | '_createdDate'
      | '_updatedDate',
    value: any[]
  ) => PackagesQueryBuilder;
  in: (
    propertyName:
      | '_id'
      | 'accountId'
      | 'externalId'
      | 'productInstances.billingInfo'
      | 'productInstances.billingInfo.type'
      | 'productInstances.billingInfo.cycleDuration'
      | 'productInstances.billingInfo.cycleDuration.unit'
      | 'productInstances.billingInfo.cycleDuration.count'
      | 'productInstances.instanceId'
      | 'productInstances.siteId'
      | 'productInstances.catalogProductId'
      | 'productInstances.status'
      | 'productInstances.failure'
      | 'productInstances.failure.code'
      | 'productInstances.failure.message'
      | 'productInstances.createdDate'
      | 'productInstances.updatedDate'
      | 'productInstances.countryCode'
      | '_createdDate'
      | '_updatedDate',
    value: any
  ) => PackagesQueryBuilder;
  exists: (
    propertyName:
      | '_id'
      | 'accountId'
      | 'externalId'
      | 'productInstances.billingInfo'
      | 'productInstances.billingInfo.type'
      | 'productInstances.billingInfo.cycleDuration'
      | 'productInstances.billingInfo.cycleDuration.unit'
      | 'productInstances.billingInfo.cycleDuration.count'
      | 'productInstances.instanceId'
      | 'productInstances.siteId'
      | 'productInstances.catalogProductId'
      | 'productInstances.status'
      | 'productInstances.failure'
      | 'productInstances.failure.code'
      | 'productInstances.failure.message'
      | 'productInstances.createdDate'
      | 'productInstances.updatedDate'
      | 'productInstances.countryCode'
      | '_createdDate'
      | '_updatedDate',
    value: boolean
  ) => PackagesQueryBuilder;
  /** @param propertyNames - Properties used in the sort. To sort by multiple properties, pass properties as additional arguments. */
  ascending: (
    ...propertyNames: Array<'_createdDate' | '_updatedDate'>
  ) => PackagesQueryBuilder;
  /** @param propertyNames - Properties used in the sort. To sort by multiple properties, pass properties as additional arguments. */
  descending: (
    ...propertyNames: Array<'_createdDate' | '_updatedDate'>
  ) => PackagesQueryBuilder;
  /** @param limit - Number of items to return, which is also the `pageSize` of the results object. */
  limit: (limit: number) => PackagesQueryBuilder;
  /** @param cursor - A pointer to specific record */
  skipTo: (cursor: string) => PackagesQueryBuilder;
  find: () => Promise<PackagesQueryResult>;
}

/**
 * @hidden
 * @fqn com.wixpress.premium.reseller.packages.v1.Packages.QueryPackages
 * @requiredField query
 */
export async function typedQueryPackages(
  query: PackageQuery
): Promise<
  NonNullablePaths<
    QueryPackagesResponse,
    `packages` | `packages.${number}._id` | `packages.${number}.accountId`,
    4
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({ query: query });

  const reqOpts = ambassadorWixPremiumResellerV1Packages.queryPackages(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { query: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['query']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface PackageQuerySpec extends QuerySpec {
  paging: 'cursor';
  wql: [
    {
      fields: ['_createdDate', '_updatedDate'];
      operators: '*';
      sort: 'BOTH';
    },
    {
      fields: [
        'productInstances',
        'productInstances._createdDate',
        'productInstances._updatedDate',
        'productInstances.billingInfo',
        'productInstances.billingInfo.cycleDuration',
        'productInstances.billingInfo.cycleDuration.count',
        'productInstances.billingInfo.cycleDuration.unit',
        'productInstances.billingInfo.type',
        'productInstances.catalogProductId',
        'productInstances.countryCode',
        'productInstances.failure',
        'productInstances.failure.code',
        'productInstances.failure.message',
        'productInstances.instanceId',
        'productInstances.siteId',
        'productInstances.status'
      ];
      operators: ['$hasAll', '$hasSome'];
      sort: 'NONE';
    },
    {
      fields: ['_id', 'accountId', 'externalId'];
      operators: '*';
      sort: 'NONE';
    }
  ];
}

export type CommonQueryWithEntityContext = QuerySdkType<
  Package,
  PackageQuerySpec
>;
export type PackageQuery = {
  /** 
  Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`.  
  */
  cursorPaging?: {
    /** 
  Maximum number of items to return in the results. 
  @max: 100 
  */
    limit?:
      | NonNullable<CommonQueryWithEntityContext['cursorPaging']>['limit']
      | null;
    /** 
  Pointer to the next or previous page in the list of results.

  Pass the relevant cursor token from the `pagingMetadata` object in the previous call's response.
  Not relevant for the first request. 
  @maxLength: 16000 
  */
    cursor?:
      | NonNullable<CommonQueryWithEntityContext['cursorPaging']>['cursor']
      | null;
  };
  /** 
  Filter object.

  Learn more about [filtering](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#filters).  
  */
  filter?: CommonQueryWithEntityContext['filter'] | null;
  /** 
  Sort object.

  Learn more about [sorting](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#sorting).  
  */
  sort?: {
    /** 
  Name of the field to sort by. 
  @maxLength: 512 
  */
    fieldName?: NonNullable<
      CommonQueryWithEntityContext['sort']
    >[number]['fieldName'];
    /** 
  Sort order.  
  */
    order?: NonNullable<CommonQueryWithEntityContext['sort']>[number]['order'];
  }[];
};

export const utils = {
  query: {
    ...createQueryUtils<Package, PackageQuerySpec, PackageQuery>(),
  },
};

/**
 * Assigns a product instance to a site.
 *
 *  You must pass the ID of the Wix account that the product instance belongs to in the header of the call.
 *
 *  The customer immediately gains access to features included in the assigned service. It's up to the reseller to decide whether assigning a product instance affects the customer’s payment.
 *
 *  It's possible to assign a product instance that's already assigned to a site, to a different site without [unassigning](/packages/unassign-product-instance-from-site) it first. But if the call fails in such a situation, the product instance may either remain assigned to the original site or become unassigned. You can confirm the instance's status by [retrieving the relevant package](/packages/get-package).
 *
 * > **Important:** This call requires an account level API key and cannot be authenticated with the standard authorization header.
 * @public
 * @requiredField identifiers
 * @requiredField identifiers.instanceId
 * @requiredField identifiers.siteId
 * @permissionId premium.reseller-package.manage
 * @fqn com.wixpress.premium.reseller.packages.v1.Packages.AssignProductInstanceToSite
 */
export async function assignProductInstanceToSite(
  identifiers: NonNullablePaths<
    AssignProductInstanceToSiteIdentifiers,
    `instanceId` | `siteId`,
    2
  >,
  options?: AssignProductInstanceToSiteOptions
): Promise<
  NonNullablePaths<
    AssignProductInstanceToSiteResponse,
    | `package._id`
    | `package.accountId`
    | `package.productInstances`
    | `package.productInstances.${number}.billingInfo.type`
    | `package.productInstances.${number}.billingInfo.cycleDuration.unit`
    | `package.productInstances.${number}.billingInfo.cycleDuration.count`
    | `package.productInstances.${number}.instanceId`
    | `package.productInstances.${number}.catalogProductId`
    | `package.productInstances.${number}.status`
    | `package.productInstances.${number}.failure.code`
    | `package.productInstances.${number}.failure.message`,
    7
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    instanceId: identifiers?.instanceId,
    siteId: identifiers?.siteId,
    idempotencyKey: options?.idempotencyKey,
  });

  const reqOpts =
    ambassadorWixPremiumResellerV1Packages.assignProductInstanceToSite(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          instanceId: '$[0].instanceId',
          siteId: '$[0].siteId',
          idempotencyKey: '$[1].idempotencyKey',
        },
        singleArgumentUnchanged: false,
      },
      ['identifiers', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface AssignProductInstanceToSiteIdentifiers {
  /**
   * ID of the product instance to assign to a site.
   * @format GUID
   */
  instanceId: string;
  /**
   * ID of the site to which the product instance is assigned.
   * @format GUID
   */
  siteId: string;
}

export interface AssignProductInstanceToSiteOptions {
  /**
   * Idempotency key.
   * @maxLength 100
   */
  idempotencyKey?: string | null;
}

/**
 * Unassigns a product instance from a site.
 *
 *  You must pass the ID of the Wix account that the product instance belongs to in the header of the call.
 *
 *  The product instance becomes floating and the customer immediately looses access to features included in the unassigned service. It's up to the reseller to decide whether unassigning a product instance affects the customer’s payment.
 *
 *  If an unassigned product instance is a requirement for another service, that service may not be available to the customer any longer. For example, if you unassign a `Combo Plan` plan from a site, a previously connected domain is disconnected.
 *
 *  You can assign floating product instances to sites using the [Assign Product Instance to Site](/packages/assign-product-instance-to-site) method.
 *
 * > **Important:** This call requires an account level API key and cannot be authenticated with the standard authorization header.
 * @param instanceId - ID of the product instance to unassign from a site.
 * @public
 * @requiredField instanceId
 * @permissionId premium.reseller-package.manage
 * @fqn com.wixpress.premium.reseller.packages.v1.Packages.UnassignProductInstanceFromSite
 */
export async function unassignProductInstanceFromSite(
  instanceId: string,
  options?: UnassignProductInstanceFromSiteOptions
): Promise<
  NonNullablePaths<
    UnassignProductInstanceFromSiteResponse,
    | `package._id`
    | `package.accountId`
    | `package.productInstances`
    | `package.productInstances.${number}.billingInfo.type`
    | `package.productInstances.${number}.billingInfo.cycleDuration.unit`
    | `package.productInstances.${number}.billingInfo.cycleDuration.count`
    | `package.productInstances.${number}.instanceId`
    | `package.productInstances.${number}.catalogProductId`
    | `package.productInstances.${number}.status`
    | `package.productInstances.${number}.failure.code`
    | `package.productInstances.${number}.failure.message`,
    7
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    instanceId: instanceId,
    idempotencyKey: options?.idempotencyKey,
  });

  const reqOpts =
    ambassadorWixPremiumResellerV1Packages.unassignProductInstanceFromSite(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          instanceId: '$[0]',
          idempotencyKey: '$[1].idempotencyKey',
        },
        singleArgumentUnchanged: false,
      },
      ['instanceId', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface UnassignProductInstanceFromSiteOptions {
  /**
   * Idempotency key.
   * @maxLength 100
   */
  idempotencyKey?: string | null;
}

/**
 * Cancels a product instance.
 *
 *
 * You must pass the ID of the Wix account that the product instance belongs
 * to in the header of the call. The call fails, if the product instance and
 * Wix account don't match.
 *
 * The customer immediately loses access to features included in the
 * canceled service. It's up to the reseller to decide whether the adjustment
 * affects the customer’s payment.
 *
 * If a canceled service is a requirement for another service, that service
 * may not be available to the customer any longer. For example, if you cancel
 * a `Combo Plan` plan, a previously connected domain is disconnected
 * from the site.
 *
 *
 * > **Important**: This call requires an account level API key and cannot be
 * > authenticated with the standard authorization header.
 * @param instanceId - ID of the product instance to cancel.
 * @public
 * @requiredField instanceId
 * @permissionId premium.reseller-package.manage
 * @fqn com.wixpress.premium.reseller.packages.v1.Packages.CancelProductInstance
 */
export async function cancelProductInstance(
  instanceId: string,
  options?: CancelProductInstanceOptions
): Promise<
  NonNullablePaths<
    CancelProductInstanceResponse,
    | `package._id`
    | `package.accountId`
    | `package.productInstances`
    | `package.productInstances.${number}.billingInfo.type`
    | `package.productInstances.${number}.billingInfo.cycleDuration.unit`
    | `package.productInstances.${number}.billingInfo.cycleDuration.count`
    | `package.productInstances.${number}.instanceId`
    | `package.productInstances.${number}.catalogProductId`
    | `package.productInstances.${number}.status`
    | `package.productInstances.${number}.failure.code`
    | `package.productInstances.${number}.failure.message`,
    7
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    instanceId: instanceId,
    idempotencyKey: options?.idempotencyKey,
  });

  const reqOpts =
    ambassadorWixPremiumResellerV1Packages.cancelProductInstance(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          instanceId: '$[0]',
          idempotencyKey: '$[1].idempotencyKey',
        },
        singleArgumentUnchanged: false,
      },
      ['instanceId', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface CancelProductInstanceOptions {
  /**
   * Idempotency key.
   * @maxLength 100
   */
  idempotencyKey?: string | null;
}

/**
 * Upgrades or downgrades a product instance. For example, you can upgrade a
 * customer's `Business Unlimited Premium` plan to `Business VIP` using this
 * endpoint.
 *
 *
 * You must pass the ID of the Wix account that the product instance belongs
 * to in the header of the call. The call fails, if the product instance and
 * Wix account don't match.
 *
 * The customer has immediate access to new features, while losing access to
 * features not included in the new service. It's up to the reseller to
 * decide whether the adjustment affects the customer’s payment.
 *
 * You can only exchange a product instance with a service of the same type.
 * Contact the [Wix B2B sales team](mailto:bizdev@wix.com) for details about
 * which services belong to the same type.
 *
 * You must provide either a new `catalogProductId` or a new `billingInfo`
 * object. The new billing cycle must be supported for the product. Contact the
 * [Wix B2B sales team](mailto:bizdev@wix.com) for information
 * about which billing cycles are supported for each product. If you adjust the
 * billing cycle for a product instance with `RECURRING` payments, you must
 * also provide `billingInfo.cycleDuration.unit`.
 *
 * If a removed feature is a requirement for another service, that service
 * may not be available to the customer any longer. For example, if you downgrade a
 * `Business Unlimited` plan to `Business Basic`, the site owners won’t be
 * able to use `Pro eCommerce Features` any longer.
 *
 * > **Important**: This call requires an account level API key and cannot be
 * > authenticated with the standard authorization header.
 * @param instanceId - ID of the product instance to adjust.
 * @public
 * @requiredField instanceId
 * @permissionId premium.reseller-package.manage
 * @fqn com.wixpress.premium.reseller.packages.v1.Packages.AdjustProductInstanceSpecifications
 */
export async function adjustProductInstanceSpecifications(
  instanceId: string,
  options?: AdjustProductInstanceSpecificationsOptions
): Promise<
  NonNullablePaths<
    AdjustProductInstanceSpecificationsResponse,
    | `package._id`
    | `package.accountId`
    | `package.productInstances`
    | `package.productInstances.${number}.billingInfo.type`
    | `package.productInstances.${number}.billingInfo.cycleDuration.unit`
    | `package.productInstances.${number}.billingInfo.cycleDuration.count`
    | `package.productInstances.${number}.instanceId`
    | `package.productInstances.${number}.catalogProductId`
    | `package.productInstances.${number}.status`
    | `package.productInstances.${number}.failure.code`
    | `package.productInstances.${number}.failure.message`,
    7
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    instanceId: instanceId,
    idempotencyKey: options?.idempotencyKey,
    catalogProductId: options?.catalogProductId,
    billingInfo: options?.billingInfo,
    discountCode: options?.discountCode,
  });

  const reqOpts =
    ambassadorWixPremiumResellerV1Packages.adjustProductInstanceSpecifications(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          instanceId: '$[0]',
          idempotencyKey: '$[1].idempotencyKey',
          catalogProductId: '$[1].catalogProductId',
          billingInfo: '$[1].billingInfo',
          discountCode: '$[1].discountCode',
        },
        singleArgumentUnchanged: false,
      },
      ['instanceId', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface AdjustProductInstanceSpecificationsOptions {
  /**
   * Idempotency key.
   * @maxLength 100
   */
  idempotencyKey?: string | null;
  /**
   * ID of the product to replace the original instance. Required in case you don't
   * provide a new `billingInfo` object.
   * @format GUID
   */
  catalogProductId?: string | null;
  /**
   * Information about the billing cycle. Required in case you don't provide a new
   * `catalogProductId`. The new billing cycle must be supported for the service.
   * Contact the [Wix B2B sales team](mailto:bizdev@wix.com) for
   * information about a service's supported billing cycles. If
   * you adjust the billing cycle for a product instance with `RECURRING`
   * payments, you must also provide `billingInfo.cycleDuration.unit`.
   */
  billingInfo?: ProductInstanceCycle;
  /**
   * Discount code indicating that the reseller provisioned the product instance
   * during a sale. In case you pass a code that isn't valid, the call succeeds and
   * no discount is applied to the product instance. Wix doesn't guarantee that a
   * discount code reduces the price between Wix and the reseller, even when it's
   * valid. You can't add a discount code after you've created the
   * product instance. Contact the [Wix B2B sales team](mailto:bizdev@wix.com) for
   * more information about supported codes.
   *
   * Max: 25 characters
   * @maxLength 25
   */
  discountCode?: string | null;
}

/**
 * Updates a package's external ID field.
 * @public
 * @requiredField identifiers
 * @requiredField identifiers.externalId
 * @requiredField identifiers.packageId
 * @permissionId PREMIUM.PACKAGE_UPDATE_EXTERNAL_ID
 * @fqn com.wixpress.premium.reseller.packages.v1.Packages.UpdatePackageExternalId
 */
export async function updatePackageExternalId(
  identifiers: NonNullablePaths<
    UpdatePackageExternalIdIdentifiers,
    `externalId` | `packageId`,
    2
  >
): Promise<
  NonNullablePaths<
    UpdatePackageExternalIdResponse,
    | `updatedPackage._id`
    | `updatedPackage.accountId`
    | `updatedPackage.productInstances`
    | `updatedPackage.productInstances.${number}.billingInfo.type`
    | `updatedPackage.productInstances.${number}.billingInfo.cycleDuration.unit`
    | `updatedPackage.productInstances.${number}.billingInfo.cycleDuration.count`
    | `updatedPackage.productInstances.${number}.instanceId`
    | `updatedPackage.productInstances.${number}.catalogProductId`
    | `updatedPackage.productInstances.${number}.status`
    | `updatedPackage.productInstances.${number}.failure.code`
    | `updatedPackage.productInstances.${number}.failure.message`,
    7
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    packageId: identifiers?.packageId,
    externalId: identifiers?.externalId,
  });

  const reqOpts =
    ambassadorWixPremiumResellerV1Packages.updatePackageExternalId(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          packageId: '$[0].packageId',
          externalId: '$[0].externalId',
        },
        singleArgumentUnchanged: false,
      },
      ['identifiers']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface UpdatePackageExternalIdIdentifiers {
  /**
   * ID of the package to update.
   * @format GUID
   */
  packageId: string;
  /**
   * External ID that will be assigned to the package.
   * @maxLength 100
   */
  externalId: string;
}
